import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '../Box'
import Flex from '../Flex'
import Copy from '../Copy'
import styled from 'styled-components'
import {
    fontSize,
    fontWeight,
    color,
} from '../../util/styles'
import { InputBorderStyles } from './InputStyles'
import MaskedSimpleInput from './MaskedSimpleInput'
import RawInput from './RawInput'
import Label from '../Label/SimpleInputLabel'
export const GRAY_COLOR = 'gray'
export const FOCUSED_TEXT_COLOR = 'darkGray'

const Container = styled(Flex)`
    ${InputBorderStyles}
    overflow: hidden;
`

Container.propTypes = {
    focused: PropTypes.bool,
    borderRadius: PropTypes.string,
    borderWidth: PropTypes.string,
    theme: PropTypes.object,
}

const IconContainer = styled(Box)`
    ${color}
    ${fontSize}
    ${fontWeight}
`

IconContainer.propTypes = {
    focused: PropTypes.bool,
    error: PropTypes.bool,
    borderRadius: PropTypes.string,
    borderWidth: PropTypes.string,
    theme: PropTypes.object,
}

const SimpleInput = ({
    valid,
    focus,
    width,
    height,
    animated,
    label,
    labelFontSize,
    labelFontWeight,
    placeholder,
    helperText,
    onChange,
    onFocus,
    onBlur,
    value,
    getFieldValue,
    errors,
    padding,
    forceFocus,
    rightIcon,
    type,
    dataTest,
    theme,
    mask,
    ...restProps
}) => {
    const inputRef = useRef(null)
    const [focused, setFocused] = useState(focus)

    useEffect(() => {
        if (focused) {
            onFocus()
        } else {
            onBlur()
        }
    }, [focused])

    useEffect(() => {
        forceFocus && setFocused(true)
    }, [forceFocus])

    const withErrors = Object.keys(errors).length !== 0

    const active = !!(focused || value)
    const labelText = label || placeholder
    // eslint-disable-next-line no-extra-boolean-cast
    return <Box width={width} maxWidth={width} position="relative" pt={!!labelText ? '20px' : undefined}>
        {!!labelText && <Label
            padding={padding}
            width={width}
            animated={animated}
            active={active}
            focus={focused}
            labelFontSize={labelFontSize}
            valid={valid}
        >{labelText}
        </Label>}
        <Container
            error={withErrors || !valid}
            focused={focused}
            width={width}
            alignItems='flex-end'>
            <Flex width="100%" >
                {mask ? <MaskedSimpleInput
                    mask={mask}
                    ref={inputRef}
                    data-test={dataTest}
                    focused={focused}
                    valid={valid}
                    padding={padding}
                    type={type}
                    error={withErrors || !valid}
                    height={height}
                    fontColor={focused ? FOCUSED_TEXT_COLOR : GRAY_COLOR}
                    value={type === 'file' ? undefined : value || ''}
                    placeholder={focused ? placeholder : labelText}
                    onChange={({ target }) => {
                        onChange && target && onChange(getFieldValue(target))
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    {...restProps} />
                    : <RawInput
                        ref={inputRef}
                        data-test={dataTest}
                        focused={focused}
                        valid={valid}
                        height={height}
                        padding={padding}
                        type={type}
                        withErrors={withErrors}
                        fontColor={focused ? FOCUSED_TEXT_COLOR : GRAY_COLOR}
                        value={type === 'file' ? undefined : value || ''}
                        placeholder={focused ? placeholder : labelText}
                        onChange={({ target }) => {
                            onChange && target && onChange(getFieldValue(target))
                        }}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        {...restProps} />
                }
            </Flex>
            {rightIcon && <IconContainer
                fontColor={focused ? FOCUSED_TEXT_COLOR : GRAY_COLOR}
                fontSize='medium'
                pb={padding}
                pt={padding}
                pr={padding}>
                {rightIcon}
            </IconContainer>}
        </Container>
        {helperText && <Copy
            key='error'
            mt={padding}
            padding='0'
            display='block'
            fontSize='xSmall'
            fontWeight={withErrors ? 'book' : 'regular'}
            fontColor={withErrors ? 'error' : GRAY_COLOR}>{helperText}</Copy>}
    </Box>
}

SimpleInput.propTypes = {
    valid: PropTypes.bool,
    focus: PropTypes.bool,
    animated: PropTypes.bool,
    rightIcon: PropTypes.node,
    height: PropTypes.string,
    width: PropTypes.string,
    label: PropTypes.node,
    labelFontSize: PropTypes.string,
    labelFontWeight: PropTypes.string,
    placeholder: PropTypes.node,
    helperText: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.string,
    getFieldValue: PropTypes.func.isRequired,
    errors: PropTypes.object,
    padding: PropTypes.any,
    forceFocus: PropTypes.bool,
    type: PropTypes.string,
    dataTest: PropTypes.string,
    theme: PropTypes.object,
    mask: PropTypes.string
}

SimpleInput.defaultProps = {
    animated: undefined,
    valid: true,
    focus: false,
    label: null,
    placeholder: null,
    helperText: null,
    rightIcon: null,
    onChange: () => { },
    onBlur: () => { },
    onFocus: () => { },
    getFieldValue: ({ value }) => value,
    errors: {},
    forceFocus: false,
    type: 'text',
    padding: '8px',
    dataTest: undefined,
}

export default SimpleInput
